// app/src/Spending.jsx — credit-card spending, categorized to cost reports.
//
// Pro + à-la-carte only. Shows CREDIT-CARD activity (not checking — that's
// already categorized in Sales/Wages/COGS reports). Each charge is either:
//   • reconciled COGS — a scanned receipt matched this charge (read-only badge)
//   • categorized to a cost report via the dropdown (COGS, Rent & Utilities,
//     Supplies, Maintenance & Repairs, Insurance, Advertising, Software, Capital
//     Expenses, Other) — applied to just the charge or every charge from the
//     merchant. COGS is pickable too, for an unscanned ingredient buy an
//     employee put on the card without a receipt.
//   • or uncategorized until the operator picks.
// "Spending" nets charges against refunds and excludes payments/transfers.
//
// v1 lives entirely on this tab: it assigns categories and shows a per-category
// breakdown. Feeding those allocations into each cost report's totals is a fast
// follow (the allocations persist in db.spendRule for that).

const NON_SPEND_CATS = new Set(['LOAN_PAYMENTS', 'TRANSFER_IN', 'TRANSFER_OUT']);
const spendLib = () => window.kanjo?.cashflow?.spend;

function centsFmt(cents, whole = false) {
  try {
    return new Intl.NumberFormat(navigator.language || 'en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
    }).format((cents || 0) / 100);
  } catch { return '$' + ((cents || 0) / 100).toFixed(whole ? 0 : 2); }
}

function parseYMD(s) {
  const [y, m, d] = String(s || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
}
function dayFmt(s) {
  const d = parseYMD(s);
  if (!d) return s || '';
  try { return new Intl.DateTimeFormat(navigator.language || 'en-US', { month: 'short', day: 'numeric' }).format(d); }
  catch { return s; }
}

function catLabel(id) {
  switch (id) {
    case 'cogs':           return t('spending.cat.cogs', 'COGS');
    case 'rent_utilities': return t('spending.cat.rentUtil', 'Rent & Utilities');
    case 'supplies':       return t('spending.cat.supplies', 'Supplies');
    case 'maintenance':    return t('spending.cat.maintenance', 'Maintenance & Repairs');
    case 'insurance':      return t('spending.cat.insurance', 'Insurance');
    case 'advertising':    return t('spending.cat.advertising', 'Advertising');
    case 'software':       return t('spending.cat.software', 'Software');
    case 'capex':          return t('spending.cat.capex', 'Capital Expenses');
    case 'taxes':          return t('spending.cat.taxes', 'Taxes');
    case 'other':          return t('spending.cat.other', 'Other');
    default:               return t('spending.cat.uncat', 'Uncategorized');
  }
}

// Category display order for the breakdown (uncategorized last, handled via '').
const CAT_ORDER = ['cogs', 'rent_utilities', 'supplies', 'maintenance', 'insurance', 'advertising', 'software', 'capex', 'taxes', 'other', ''];

function windowISO() {
  const today = new Date();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const monday = new Date(today);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const thirtyAgo = new Date(today);
  thirtyAgo.setDate(thirtyAgo.getDate() - 29);
  // Prior-30 window: the 30 days ending the day before the current window starts
  // (for the last-30 vs prior-30 comparison). sixtyAgo..(monthISO − 1 day).
  const sixtyAgo = new Date(today);
  sixtyAgo.setDate(sixtyAgo.getDate() - 59);
  const priorEnd = new Date(thirtyAgo);
  priorEnd.setDate(priorEnd.getDate() - 1);
  // Calendar-month windows for the [This month]/[Last month] periods, plus the
  // month before last (the last-month delta baseline) and "last month to the
  // same day" (a fair month-to-date comparison — MTD vs a FULL month always
  // reads as a drop).
  const y = today.getFullYear(), m = today.getMonth();
  const thisMonthStart = new Date(y, m, 1);
  const lastMonthStart = new Date(y, m - 1, 1);
  const lastMonthEnd = new Date(y, m, 0);
  const prevPrevStart = new Date(y, m - 2, 1);
  const prevPrevEnd = new Date(y, m - 1, 0);
  const lastMonthSameDay = new Date(y, m - 1, Math.min(today.getDate(), lastMonthEnd.getDate()));
  return {
    todayISO: iso(today), weekISO: iso(monday), monthISO: iso(thirtyAgo), sixtyISO: iso(sixtyAgo), priorEndISO: iso(priorEnd),
    thisMonthStartISO: iso(thisMonthStart), lastMonthStartISO: iso(lastMonthStart), lastMonthEndISO: iso(lastMonthEnd),
    prevPrevStartISO: iso(prevPrevStart), prevPrevEndISO: iso(prevPrevEnd), lastMonthSameDayISO: iso(lastMonthSameDay),
  };
}

// Per-category slice colors for the donut + legend. Hues are spread around the
// wheel so no two categories — especially adjacent legend rows — read alike:
// COGS(amber) vs Rent(blue) and Supplies(pink) vs Capex(violet) sit on opposite
// sides. Saturated 500/600 shades read as fills on both the white (light) and
// raised (dark) card.
const SPEND_COLORS = {
  cogs:           '#F59E0B', // amber
  rent_utilities: '#2563EB', // blue
  supplies:       '#EC4899', // pink
  maintenance:    '#14B8A6', // teal
  insurance:      '#16A34A', // green
  advertising:    '#EF4444', // red
  software:       '#06B6D4', // cyan
  capex:          '#8B5CF6', // violet
  other:          '#64748B', // slate
  '':             '#9AA0A6', // uncategorized (neutral grey)
};

// The standard expense color (matches KMoney tone="danger" across the reports).
// A charge is an expense → red; a refund/credit is money back → green.
const EXPENSE_RED = 'var(--danger,#d2563f)';
const REFUND_GREEN = 'var(--positive-ink,#2e9e5b)';
const amountColor = (cents) => (cents < 0 ? REFUND_GREEN : EXPENSE_RED);

// A donut chart for the by-category breakdown. `slices` = [{id,cents}] with
// positive cents (refund-negative categories are filtered by the caller).
function CategoryDonut({ slices, total, label }) {
  const R = 52, SW = 20, C = 2 * Math.PI * R, cx = 64, cy = 64;
  let acc = 0;
  const arcs = slices.map((s) => {
    const frac = total > 0 ? s.cents / total : 0;
    const dash = frac * C;
    const seg = { id: s.id, dash, gap: C - dash, offset: -acc * C };
    acc += frac;
    return seg;
  });
  return (
    <svg width="128" height="128" viewBox="0 0 128 128" style={{ flexShrink: 0 }} role="img" aria-label={t('spending.byCategory', 'By category')}>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--surface-3)" strokeWidth={SW} />
      {arcs.map((a) => (
        <circle key={a.id || 'uncat'} cx={cx} cy={cy} r={R} fill="none"
          stroke={SPEND_COLORS[a.id] || '#888'} strokeWidth={SW}
          strokeDasharray={`${a.dash} ${a.gap}`} strokeDashoffset={a.offset}
          transform={`rotate(-90 ${cx} ${cy})`} />
      ))}
      <text x={cx} y={cy - 2} textAnchor="middle" style={{ fontSize: 15, fontWeight: 700, fill: 'var(--ink)' }}>{centsFmt(total, true)}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 9, fill: 'var(--ink-3)' }}>{label || t('spending.last30', 'Last 30 days')}</text>
    </svg>
  );
}

// Signed percent-change chip vs the period's baseline. For SPENDING, up is bad
// (red), down is good (green) — the reverse of revenue.
function DeltaChip({ pct, label }) {
  if (pct == null) return null;
  const up = pct > 0.5, down = pct < -0.5;
  const color = up ? 'var(--danger,#d2563f)' : down ? 'var(--positive,#2e9e5b)' : 'var(--ink-3)';
  const arrow = up ? '▲' : down ? '▼' : '·';
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color }}>
      {arrow} {Math.abs(pct).toFixed(pct < 10 && pct > -10 ? 1 : 0)}%
      <span style={{ fontWeight: 500, color: 'var(--ink-3)' }}> {label || t('spending.vsPrior30', 'vs prior 30 d')}</span>
    </span>
  );
}

function useSpendingData() {
  const [st, setSt] = React.useState({ loading: true });
  React.useEffect(() => {
    let live = true;
    (async () => {
      const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
      if (!claims) { if (live) setSt({ loading: false, entitled: false }); return; }
      const tier = String(claims['custom:tier'] || 'free').toLowerCase();
      let groups = claims['cognito:groups'];
      if (typeof groups === 'string') groups = groups.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
      const isAdmin = Array.isArray(groups) && groups.includes('admins');
      const entitled = isAdmin || tier === 'pro' || tier === 'payg';
      // Entitlement is settled from the JWT alone; a data error below must not
      // demote a paying user to the paywall.
      if (!entitled) { if (live) setSt({ loading: false, entitled: false, tier }); return; }

      try {
        const db = window.kanjo.db;
        const spend = spendLib();
        // Match scanned COGS receipts against the card feed so paid-by-card
        // invoices show as reconciled. Idempotent + best-effort.
        try { await window.kanjo.reconciler?.reconcileAll?.(); } catch { /* non-fatal */ }

        const accounts = await db.plaidAccounts.where('userId').equals(claims.sub).toArray();
        const cardById = new Map();
        for (const a of accounts) if (a.type === 'credit') cardById.set(a.plaidAccountId, a);
        const hasCard = cardById.size > 0;

        const win = windowISO();
        const all = await db.plaidTransactions.where('userId').equals(claims.sub).toArray();
        // Card activity back to the month BEFORE last (the widest baseline any
        // period toggle needs), excluding payments/transfers. Each period —
        // this month / last month / last 30 days — filters this in render.
        const from = win.prevPrevStartISO < win.sixtyISO ? win.prevPrevStartISO : win.sixtyISO;
        const cardAll = all.filter((tx) =>
          cardById.has(tx.plaidAccountId) && !NON_SPEND_CATS.has(tx.category) &&
          tx.date >= from && tx.date <= win.todayISO);

        // Checking outflows nothing has accounted for yet (the plumber's check,
        // the one-off repair). Loaded over the SAME widest window the period
        // toggle can ask for, then filtered per period in render — same as the
        // card feed above, so switching periods needs no round-trip.
        const unallocAll = await window.kanjo.cashflow.unallocated
          .listUnallocated(claims.sub, { from, to: win.todayISO })
          .catch(() => []);

        const [reconciledByTx, rules] = await Promise.all([
          spend.getReconciledCogs(claims.sub),
          spend.getRules(claims.sub),
        ]);

        if (live) setSt({
          loading: false, entitled: true, tier, userId: claims.sub, hasCard,
          cardAll, unallocAll, reconciledByTx, rules, win,
          hasBank: accounts.some((a) => a.type !== 'credit'),
          // Per-card running balance (persisted by the balances WAL on connect +
          // every sync); shown when available.
          cards: [...cardById.values()],
        });
      } catch (e) {
        if (live) setSt({ loading: false, entitled: true, tier, loadError: true, error: String(e?.message || e) });
      }
    })();
    return () => { live = false; };
  }, []);
  return [st, setSt];
}

function SpendingTile({ label, cents, count, delta, deltaLabel, accent, sub }) {
  return (
    <div className="card" style={{ padding: 20, flex: 1, minWidth: 0 }}>
      <div className="label" style={{ marginBottom: 10 }}>{label}</div>
      <div className="figure" style={{ fontSize: 28, color: accent || 'var(--ink)' }}>{centsFmt(cents, true)}</div>
      <div style={{ marginTop: 6, color: 'var(--ink-3)', fontSize: 12.5 }}>
        {delta !== undefined ? <DeltaChip pct={delta} label={deltaLabel} />
          : sub != null ? sub
          : <>{count} {count === 1 ? t('spending.txnOne', 'transaction') : t('spending.txnMany', 'transactions')}</>}
      </div>
    </div>
  );
}

function CategorySelect({ value, onPick }) {
  const opts = spendLib()?.PICKABLE_CATEGORIES || [];
  return (
    <select
      value={value || ''}
      onChange={(e) => onPick(e.target.value)}
      style={{
        fontSize: 12.5, padding: '5px 8px', borderRadius: 8,
        border: '1px solid var(--rule)', background: 'var(--surface-2)',
        color: value ? 'var(--ink)' : 'var(--ink-3)', maxWidth: 160,
      }}>
      <option value="">{t('spending.pick', 'Categorize…')}</option>
      {opts.map((c) => <option key={c.id} value={c.id}>{catLabel(c.id)}</option>)}
    </select>
  );
}

function TxnRow({ row, first, onPick }) {
  const { tx, cat, reconciled, vendor } = row;
  const credit = tx.amountCents < 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
      borderTop: first ? 'none' : '1px solid var(--rule)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tx.merchant}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          {dayFmt(tx.date)}{tx.acct ? ` · ${tx.acct}` : ''}
          {tx.pending ? ` · ${t('spending.pending', 'pending')}` : ''}
          {credit ? ` · ${t('spending.refund', 'refund')}` : ''}
        </div>
        <div style={{ marginTop: 6 }}>
          {reconciled ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
              color: 'var(--positive-ink)', background: 'var(--positive-bg)', padding: '3px 9px', borderRadius: 8,
            }}>
              ✓ {t('spending.cat.cogs', 'COGS')}{vendor ? ` · ${vendor}` : ''}
            </span>
          ) : (
            <CategorySelect value={cat} onPick={(v) => onPick(tx, v)} />
          )}
        </div>
      </div>
      <div className="figure" style={{ fontSize: 15, color: amountColor(tx.amountCents) }}>
        {centsFmt(tx.amountCents)}
      </div>
    </div>
  );
}

// Bottom sheet shown after a category is picked: apply to just this charge, or
// to every charge from this merchant.
function ScopeSheet({ tx, category, onChoose, onCancel }) {
  useT();
  const merchant = tx.merchant || tx.merchantName || t('spending.thisMerchant', 'this merchant');
  const hasMerchant = !!tx.merchantName;
  const btn = { width: '100%', padding: '12px', borderRadius: 10, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', marginBottom: 10 };
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 440, margin: 10, marginBottom: 'calc(env(safe-area-inset-bottom, 0) + 10px)', padding: 18, borderRadius: 18 }}>
        <div style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 }}>
          {t('spending.applyAs', 'Categorize as')} {catLabel(category)}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginBottom: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {merchant} · {centsFmt(tx.amountCents)}
        </div>
        <button onClick={() => onChoose('one')} style={{ ...btn, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', border: 'none' }}>
          {t('spending.scopeOne', 'Just this charge')}
        </button>
        {hasMerchant && (
          <button onClick={() => onChoose('all')} style={{ ...btn, background: 'var(--surface-2)', color: 'var(--ink)', border: '1px solid var(--rule)' }}>
            {t('spending.scopeAll', 'Every charge from')} {merchant}
          </button>
        )}
        <button onClick={onCancel} style={{ width: '100%', padding: '10px', background: 'transparent', border: 'none', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
}

function SpendingScreen({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const [st, setSt] = useSpendingData();
  const [pending, setPending] = React.useState(null); // { tx, category } → scope sheet
  const [period, setPeriod] = React.useState('month'); // 'month' | 'lastMonth' | '30d'

  // Apply a category at a chosen scope: 'one' (per-charge override), 'all'
  // (per-merchant rule), or 'clear' (remove both). Updates local state + persists.
  const applyScope = React.useCallback(async (tx, category, scope) => {
    const spend = spendLib();
    const txK = 'tx:' + tx.plaidTxId;
    const mK = spend.ruleKey(tx);
    setSt((prev) => {
      const rules = new Map(prev.rules);
      if (scope === 'clear' || !category) { rules.delete(txK); rules.delete(mK); }
      else if (scope === 'all') { rules.set(mK, category); rules.delete(txK); }
      else { rules.set(txK, category); }
      return { ...prev, rules };
    });
    try {
      if (scope === 'clear' || !category) { await spend.setRule(st.userId, txK, null); await spend.setRule(st.userId, mK, null); }
      else if (scope === 'all') { await spend.setRule(st.userId, mK, category); await spend.setRule(st.userId, txK, null); }
      else { await spend.setRule(st.userId, txK, category); }
    } catch { /* keep UI responsive */ }
    setPending(null);
  }, [st.userId, setSt]);

  // Picking a category opens the scope sheet (this charge vs all of the
  // merchant); clearing removes the rules immediately.
  const onPickCategory = React.useCallback((tx, category) => {
    if (!category) applyScope(tx, '', 'clear');
    else setPending({ tx, category });
  }, [applyScope]);

  let body;
  if (st.loading) {
    body = (
      <>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="card" style={{ height: 104, flex: 1 }} />
          <div className="card" style={{ height: 104, flex: 1 }} />
        </div>
        <div className="card" style={{ height: 240 }} />
      </>
    );
  } else if (!st.entitled) {
    body = (
      <div className="card" style={{ padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
          {t('spending.lockedTitle', 'Spending is a Pro feature')}
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
          {t('spending.lockedBody', 'Connect a credit card and watch your card spending. Available on the Pro and à-la-carte plans.')}
        </div>
        <button className="btn primary" onClick={() => nav.navigate('/account/subscribe')} style={{ width: '100%' }}>
          {t('spending.seePlans', 'See plans')}
        </button>
      </div>
    );
  } else if (st.loadError) {
    body = (
      <div className="card" style={{ padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
          {t('spending.errTitle', "Couldn't load spending")}
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          {t('spending.errBody', 'Something went wrong reading your card activity. Reopen the tab to try again.')}
        </div>
      </div>
    );
  } else if (!st.hasCard && !st.hasBank) {
    body = (
      <div className="card" style={{ padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
          {t('spending.connectTitle', 'Connect a credit card')}
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
          {t('spending.connectBody', 'Link a credit card and your card charges show up here — this week and the last 30 days. Checking-account activity stays in your reports.')}
        </div>
        <button className="btn primary" onClick={() => nav.navigate('/account')} style={{ width: '100%' }}>
          {t('spending.connect', 'Connect a card')}
        </button>
      </div>
    );
  } else {
    const spend = spendLib();
    const w = st.win;
    // Period windows: each has its range, display label, and the baseline the
    // delta compares against. Month-to-date compares against last month TO THE
    // SAME DAY (MTD vs a full month always reads as a drop); last month
    // compares against the month before it; last-30 against the prior 30.
    const PERIODS = {
      month: {
        label: t('period.thisMonth', 'This month'), from: w.thisMonthStartISO, to: w.todayISO,
        prior: { from: w.lastMonthStartISO, to: w.lastMonthSameDayISO },
        deltaLabel: t('spending.vsLastMonthToDate', 'vs last month to date'),
      },
      lastMonth: {
        label: t('period.lastMonth', 'Last month'), from: w.lastMonthStartISO, to: w.lastMonthEndISO,
        prior: { from: w.prevPrevStartISO, to: w.prevPrevEndISO },
        deltaLabel: t('spending.vsPriorMonth', 'vs prior month'),
      },
      '30d': {
        label: t('spending.last30', 'Last 30 days'), from: w.monthISO, to: w.todayISO,
        prior: { from: w.sixtyISO, to: w.priorEndISO },
        deltaLabel: t('spending.vsPrior30', 'vs prior 30 d'),
      },
    };
    const P = PERIODS[period] || PERIODS.month;
    const inRange = (tx, r) => tx.date >= r.from && tx.date <= r.to;
    const cardTxs = st.cardAll.filter((tx) => inRange(tx, P));
    const priorTxs = st.cardAll.filter((tx) => inRange(tx, P.prior));

    // Checking outflows still waiting on a category. Re-filtered against the
    // live rules map so a row leaves the list the moment it's categorized,
    // without reloading the tab.
    const unallocRows = (st.unallocAll || [])
      .filter((r) => inRange(r, P))
      .filter((r) => !st.rules.get('tx:' + r.plaidTxId) && !st.rules.get(spend.ruleKey(r)));
    const unallocCents = unallocRows.reduce((s, r) => s + r.amountCents, 0);

    // Effective category per charge, newest first.
    const rows = cardTxs
      .slice()
      .sort((a, b) => (b.date < a.date ? -1 : b.date > a.date ? 1 : 0))
      .map((tx) => {
        const rec = st.reconciledByTx.get(tx.plaidTxId);
        // Precedence: reconciled COGS > per-charge override > per-merchant rule.
        // No keyword auto-guess — a charge stays blank until the owner picks.
        const cat = rec ? 'cogs' : (st.rules.get('tx:' + tx.plaidTxId) || st.rules.get(spend.ruleKey(tx)) || '');
        // Demo mode obfuscates identifying names at the point they enter the
        // row model, so every downstream display (list, scope sheet) shows the
        // same fictional value. Merchant routes through cleanTxnName — the same
        // chokepoint Cashflow/Today/Reports use — so a given merchant reads
        // identically everywhere. Off-demo, cleanTxnName passes card merchants
        // through unchanged, so this is a no-op.
        const clean = window.kanjo?.cashflow?.util?.cleanTxnName || ((s) => s);
        const ob = window.kanjo?.demo?.obscure || ((s) => s);
        return {
          cat, reconciled: !!rec, vendor: rec ? ob(rec.vendor, 'vendor') : '',
          tx: {
            plaidTxId: tx.plaidTxId, date: tx.date, amountCents: tx.amountCents,
            merchant: clean(tx.merchantName) || t('spending.rowFallback', 'Card charge'),
            merchantName: tx.merchantName, pending: !!tx.pending,
            acct: '', // account mask omitted in the categorized list to save room
          },
        };
      });

    // Effective category of any card tx, for splitting capex out and building
    // the breakdown. Reconciled COGS > per-charge override > per-merchant rule > blank.
    const catOf = (tx) => st.reconciledByTx.get(tx.plaidTxId) ? 'cogs'
      : (st.rules.get('tx:' + tx.plaidTxId) || st.rules.get(spend.ruleKey(tx)) || '');
    // OPERATING spend excludes capex (one-off capital items, tracked in the Capex
    // report); the period-vs-baseline comparison is operating-only.
    const opSum = (txs) => txs.reduce((s, tx) => catOf(tx) === 'capex' ? s : s + tx.amountCents, 0);
    const opLast = opSum(cardTxs), opPrior = opSum(priorTxs);
    const opCount = cardTxs.filter((tx) => catOf(tx) !== 'capex').length;
    const delta = opPrior > 0 ? ((opLast - opPrior) / opPrior) * 100 : null;
    const capexTxs = cardTxs.filter((tx) => catOf(tx) === 'capex');
    const capexCents = capexTxs.reduce((s, tx) => s + tx.amountCents, 0);

    // Per-category breakdown over the selected period (donut = positive slices).
    const byCat = new Map();
    for (const r of rows) byCat.set(r.cat, (byCat.get(r.cat) || 0) + r.tx.amountCents);
    const breakdown = CAT_ORDER.filter((id) => byCat.has(id)).map((id) => ({ id, cents: byCat.get(id) }));
    const donutSlices = breakdown.filter((b) => b.cents > 0);
    const donutTotal = donutSlices.reduce((s, b) => s + b.cents, 0);

    body = (
      <>
        {/* Period toggle — drives the tiles, the by-category breakdown, and the
            charge list below (categorizing last month's charges is the use case). */}
        <div role="tablist" aria-label="Period" style={{ display: 'flex', gap: 4, padding: 4,
          background: 'var(--surface-3,#f0f0f3)', borderRadius: 12 }}>
          {[['month', PERIODS.month.label], ['lastMonth', PERIODS.lastMonth.label], ['30d', PERIODS['30d'].label]].map(([k, lbl]) => {
            const on = period === k;
            return (
              <button key={k} role="tab" aria-selected={on} onClick={() => setPeriod(k)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none',
                  background: on ? 'var(--surface,#fff)' : 'transparent',
                  color: on ? 'var(--ink)' : 'var(--ink-3)',
                  fontWeight: on ? 600 : 500, fontSize: 13, cursor: 'pointer',
                  boxShadow: on ? '0 1px 2px rgba(0,0,0,0.04)' : 'none' }}>{lbl}</button>
            );
          })}
        </div>

        {/* Card tiles only mean something with a card connected — a
            checking-only book still gets the unallocated list below. */}
        {st.hasCard && (
          <div style={{ display: 'flex', gap: 12 }}>
            <SpendingTile label={P.label} cents={opLast}
              accent={EXPENSE_RED} count={opCount}
              delta={delta == null ? undefined : delta} deltaLabel={P.deltaLabel} />
            <KLink to="/reports/capex" asButton style={{ flex: 1, display: 'flex' }}>
              <SpendingTile label={t('spending.cat.capex', 'Capital Expenses')} cents={capexCents}
                accent={EXPENSE_RED} count={capexTxs.length} />
            </KLink>
          </div>
        )}

        {/* Checking outflows nothing has filed yet — the plumber's check, a
            one-off repair, a supplier paid by check that never got scanned.
            Card charges are NOT included: they have their own list below, and
            counting both would double the same spend against the card bill that
            settles it. Sits above everything else because it's the only part of
            this screen with something to do. */}
        {st.hasBank && (
          <div className="card" style={{ padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
              <div className="label">{t('spending.unallocated', 'Not allocated')} · {P.label}</div>
              <div className="figure" style={{ fontSize: 20, color: unallocRows.length ? EXPENSE_RED : 'var(--ink-3)' }}>
                {centsFmt(unallocCents, true)}
              </div>
            </div>
            {unallocRows.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
                {t('spending.unallocatedNone', 'Every checking payment in this period is accounted for.')}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '6px 0 4px', lineHeight: 1.5 }}>
                  {unallocRows.length} {unallocRows.length === 1
                    ? t('spending.unallocatedOne', 'checking payment has no cost report yet')
                    : t('spending.unallocatedMany', 'checking payments have no cost report yet')}
                  {' — '}{t('spending.unallocatedHint', "payroll, taxes, owner draws and card bills are filed already, so what's left is spend nobody has categorized.")}
                </div>
                {unallocRows.map((r, i) => (
                  <React.Fragment key={r.plaidTxId}>
                    <TxnRow first={i === 0} onPick={onPickCategory}
                      row={{
                        cat: r.guessCat || '', reconciled: false, vendor: '',
                        tx: {
                          plaidTxId: r.plaidTxId, date: r.date, amountCents: r.amountCents,
                          merchant: (window.kanjo?.cashflow?.util?.cleanTxnName?.(r.rawName) || r.rawName
                                     || t('spending.bankRowFallback', 'Checking payment')),
                          merchantName: r.merchantName, pending: false,
                          acct: window.kanjo?.demo?.obscure?.(r.acctName, 'account') || r.acctName,
                        },
                      }} />
                    {r.supplierName && (
                      <div style={{ fontSize: 11.5, color: 'var(--warning)', lineHeight: 1.45, padding: '0 0 8px' }}>
                        {t('spending.looksLikeSupplier', 'Looks like a payment to')} <strong>{window.kanjo?.demo?.obscure?.(r.supplierName, 'vendor') || r.supplierName}</strong>
                        {' — '}{t('spending.looksLikeSupplierHint', "if the invoice behind it was scanned it's already in COGS, so tagging this COGS too would count it twice.")}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        )}

        {/* By category — above the card balances (owner request 2026-07-11). */}
        {breakdown.length > 0 && (
          <div className="card" style={{ padding: '14px 18px' }}>
            <div className="label" style={{ margin: '0 0 12px' }}>{t('spending.byCategory', 'By category')} · {P.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {donutSlices.length > 0 && <CategoryDonut slices={donutSlices} total={donutTotal} label={P.label} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                {breakdown.map((b) => (
                  <div key={b.id || 'uncat'} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: SPEND_COLORS[b.id] || '#888', flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: b.id === '' ? 'var(--ink-3)' : 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catLabel(b.id)}</span>
                    <span className="figure" style={{ fontSize: 13, color: amountColor(b.cents), flexShrink: 0 }}>{centsFmt(b.cents)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Per-card running balance — what's currently owed on each card. */}
        {(st.cards || []).some((c) => c.currentBalanceCents != null) && (
          <div className="card" style={{ padding: '10px 18px' }}>
            <div className="label" style={{ margin: '6px 0 2px' }}>{t('spending.cardBalances', 'Card balances')}</div>
            {(st.cards || []).filter((c) => c.currentBalanceCents != null).map((c, i) => (
              <div key={c.plaidAccountId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid var(--rule)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(window.kanjo?.demo?.obscure?.(c.name, 'account') || c.name) || t('spending.cardFallback', 'Card')}{c.mask ? ` ···· ${window.kanjo?.demo?.obscure?.(c.mask, 'mask') || c.mask}` : ''}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1 }}>
                    {c.limitCents ? `${t('spending.ofLimit', 'of')} ${centsFmt(c.limitCents, true)} ${t('spending.limit', 'limit')}` : t('spending.balanceOwed', 'current balance')}
                    {c.balanceAsOf ? ` · ${dayFmt(c.balanceAsOf)}` : ''}
                  </div>
                </div>
                <div className="figure" style={{ fontSize: 15, flexShrink: 0 }}>{centsFmt(c.currentBalanceCents)}</div>
              </div>
            ))}
          </div>
        )}

        {st.hasCard && (
          <div className="card" style={{ padding: '4px 18px' }}>
            {rows.length === 0 ? (
              <div style={{ padding: '20px 0', color: 'var(--ink-3)', fontSize: 14, textAlign: 'center' }}>
                {t('spending.emptyPeriod', 'No card activity in this period.')}
              </div>
            ) : (
              rows.map((r, i) => <TxnRow key={r.tx.plaidTxId || i} row={r} first={i === 0} onPick={onPickCategory} />)
            )}
          </div>
        )}

        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, padding: '2px 4px' }}>
          {t('spending.note', 'COGS is matched automatically from scanned receipts — or pick it yourself for an ingredient buy that never got scanned. Pick a cost report for everything else — you choose whether it applies to just that charge or every charge from that merchant. Payments and transfers are excluded.')}
        </div>
      </>
    );
  }

  return (
    <KShell dark={dark} tab="spending" persona={persona}>
      <KHeader back big title={t('spending.title', 'Spending')} />
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {body}
      </div>
      {pending && (
        <ScopeSheet
          tx={pending.tx}
          category={pending.category}
          onChoose={(scope) => applyScope(pending.tx, pending.category, scope)}
          onCancel={() => setPending(null)}
        />
      )}
    </KShell>
  );
}

// CategorySelect + ScopeSheet + the label helper are shared with the Today
// tab's Recent-activity tile (unclassified transactions get the same picker).
Object.assign(window, { SpendingScreen, CategorySelect, ScopeSheet, spendCatLabel: catLabel });
